import { useState } from 'react';
import { TabContext, TabPanel } from '@mui/lab';
import { Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { ExternalUrls } from '@/constants';
import type { EduplayCourse } from '@/data/eduplay/interfaces';
import { useIndexMyCourses } from '@/data/eduplay/my-courses';
import { useSetTitle } from '@/hooks/use-set-title';
import { EduplayCourseList } from '@/packages/eduplay/components/course-list';

enum TabIds {
  inProgress = 'inProgress',
  completed = 'completed',
}

const TabsConfig = {
  inProgress: { label: 'Cursos em andamento' },
  completed: { label: 'Cursos concluídos' },
};

const getCourses = (courses: EduplayCourse[]) => {
  if (!courses) return { completedCourses: [], inProgressCourses: [] };

  return courses.reduce(
    (acc, course) => {
      if (course.is_completed) {
        acc.completedCourses.push(course);
      } else {
        acc.inProgressCourses.push(course);
      }
      return acc;
    },
    {
      completedCourses: [] as EduplayCourse[],
      inProgressCourses: [] as EduplayCourse[],
    }
  );
};

export const Eduplay = () => {
  const [value, setValue] = useState(TabIds.inProgress);
  useSetTitle(`Meu Aprendizado | ${TabsConfig[value]?.label}`);

  const { data, isLoading } = useIndexMyCourses();
  const { completedCourses, inProgressCourses } = getCourses(data || []);

  return (
    <PageContainer>
      <PageHeader title="Meu Aprendizado" />
      <TabContext value={value}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={4}
        >
          <Tabs value={value} onChange={(_, newValue) => setValue(newValue)}>
            <Tab
              label={TabsConfig.inProgress.label}
              value={TabIds.inProgress}
            />
            <Tab label={TabsConfig.completed.label} value={TabIds.completed} />
          </Tabs>
          <Typography sx={{ minWidth: 0 }}>
            Tem dúvidas ou precisa de ajuda com progresso ou certificados?{' '}
            <Link href={ExternalUrls.Support} target="_blank">
              Fale com nosso time
            </Link>
          </Typography>
        </Stack>
        <TabPanel value={TabIds.inProgress} sx={{ p: '0px' }}>
          <EduplayCourseList
            courses={inProgressCourses}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={TabIds.completed} sx={{ p: '0px' }}>
          <EduplayCourseList
            courses={completedCourses}
            isLoading={isLoading}
            isCompleted={true}
          />
        </TabPanel>
      </TabContext>
    </PageContainer>
  );
};
